import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "./components/AdminLayout";
import { getDashboardData } from "@/utils/adminApi";

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_listings: 0,
    total_views: 0,
    today_listings: 0,
    today_views: 0,
    total_users: 0,
    today_users: 0,
    total_categories: 0,
    pending_listings: 0,
    pending_reports: 0,
    vip_users: 0,
  });
  const [categoryStats, setCategoryStats] = useState<{ name: string; count: number; percent: number }[]>([]);
  const [recentListings, setRecentListings] = useState<any[]>([]);
  const [recentUsers, setRecentUsers] = useState<any[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const data = await getDashboardData();
      setStats(data.stats);
      setCategoryStats(data.category_stats || []);
      setRecentListings(data.recent_listings || []);
      setRecentUsers(data.recent_users || []);
    } catch (error) {
      console.error("加载仪表盘数据失败:", error);
    } finally {
      setLoading(false);
    }
  };

  // 计算变化百分比（模拟数据）
  const getChangePercent = (value: number) => {
    if (value === 0) return "+0";
    return `+${value}`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-xl font-bold text-gray-800">数据概览</h1>
            <p className="text-sm text-gray-500 mt-1">欢迎回来，今日数据一览</p>
          </div>
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full"></div>
            <span className="ml-3 text-gray-500">加载中...</span>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Title */}
        <div>
          <h1 className="text-xl font-bold text-gray-800">数据概览</h1>
          <p className="text-sm text-gray-500 mt-1">欢迎回来，今日数据一览</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-blue-50">
                <i className="ri-file-list-3-line text-lg text-blue-600" />
              </div>
              <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium">
                {getChangePercent(stats.today_listings)}
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.total_listings.toLocaleString()}</div>
            <div className="text-sm text-gray-500 mt-1">信息总数</div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-green-50">
                <i className="ri-user-3-line text-lg text-green-600" />
              </div>
              <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium">
                {getChangePercent(stats.today_users)}
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.total_users.toLocaleString()}</div>
            <div className="text-sm text-gray-500 mt-1">注册用户</div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-orange-50">
                <i className="ri-eye-line text-lg text-orange-600" />
              </div>
              <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium">
                {stats.today_views > 0 ? "+" : "0"}
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.today_views > 0 ? stats.today_views.toLocaleString() : "-"}</div>
            <div className="text-sm text-gray-500 mt-1">今日浏览</div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-yellow-50">
                <i className="ri-vip-crown-line text-lg text-yellow-600" />
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.vip_users.toLocaleString()}</div>
            <div className="text-sm text-gray-500 mt-1">VIP会员</div>
          </div>
        </div>

        {/* 快捷统计 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-purple-50">
              <i className="ri-time-line text-purple-600 text-sm" />
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">{stats.pending_listings}</div>
              <div className="text-xs text-gray-500">待审核信息</div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50">
              <i className="ri-error-warning-line text-red-500 text-sm" />
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">{stats.pending_reports}</div>
              <div className="text-xs text-gray-500">待处理举报</div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-cyan-50">
              <i className="ri-folder-line text-cyan-600 text-sm" />
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">{stats.total_categories}</div>
              <div className="text-xs text-gray-500">服务分类</div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-50">
              <i className="ri-eye-line text-teal-600 text-sm" />
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">{stats.total_views.toLocaleString()}</div>
              <div className="text-xs text-gray-500">总浏览量</div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Listings */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-800">最新发布信息</h2>
              <Link to="/admin/listings" className="text-sm text-orange-500 hover:text-orange-600">
                查看全部
              </Link>
            </div>
            {recentListings.length > 0 ? (
              <div className="divide-y divide-gray-50">
                {recentListings.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">{item.title}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400">{item.author}</span>
                        <span className="text-gray-300">·</span>
                        <span className="text-xs text-gray-400">{item.category}</span>
                        <span className="text-gray-300">·</span>
                        <span className="text-xs text-gray-400">{item.time}</span>
                      </div>
                    </div>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                        item.status === "已发布"
                          ? "bg-green-50 text-green-600"
                          : item.status === "待审核"
                          ? "bg-yellow-50 text-yellow-600"
                          : "bg-gray-50 text-gray-500"
                      }`}
                    >
                      {item.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-5 py-10 text-center text-sm text-gray-400">暂无数据</div>
            )}
          </div>

          {/* Category Stats */}
          <div className="bg-white rounded-xl border border-gray-100">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-800">分类信息分布</h2>
            </div>
            {categoryStats.length > 0 ? (
              <div className="p-5 space-y-4">
                {categoryStats.map((cat, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm text-gray-700">{cat.name}</span>
                      <span className="text-sm font-medium text-gray-800">{cat.count}</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-orange-400 rounded-full transition-all"
                        style={{ width: `${cat.percent}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-5 text-center text-sm text-gray-400">暂无数据</div>
            )}
          </div>
        </div>

        {/* Recent Users */}
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">最新注册用户</h2>
            <Link to="/admin/users" className="text-sm text-orange-500 hover:text-orange-600">
              查看全部
            </Link>
          </div>
          {recentUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-gray-400 border-b border-gray-50">
                    <th className="text-left px-5 py-3 font-medium">用户</th>
                    <th className="text-left px-5 py-3 font-medium">手机号</th>
                    <th className="text-left px-5 py-3 font-medium">注册时间</th>
                    <th className="text-left px-5 py-3 font-medium">发布数</th>
                    <th className="text-left px-5 py-3 font-medium">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {recentUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                            <i className="ri-user-line text-orange-500 text-sm" />
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="text-sm font-medium text-gray-800">{u.nickname}</div>
                            {u.is_vip && (
                              <i className="ri-vip-crown-fill text-yellow-500 text-xs" title="VIP会员" />
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600">{u.phone || "-"}</td>
                      <td className="px-5 py-3 text-sm text-gray-600">{u.join_time}</td>
                      <td className="px-5 py-3">
                        <span className="text-sm font-medium text-gray-800">{u.listings}</span>
                      </td>
                      <td className="px-5 py-3">
                        <Link to={`/admin/users`} className="text-xs text-orange-500 hover:text-orange-600">
                          查看详情
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-5 py-10 text-center text-sm text-gray-400">暂无数据</div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
